// Client for the folder + organize endpoints (CONTRACTS §2 — POST /folder,
// /folder-access, /organize). Folders/* are server-side only (firestore.rules
// denies client writes), so the workspace UI calls these Cloud Functions. The
// __session cookie rides along automatically on the same-origin request.

import type { AccessChange } from "./access";

async function errorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const data = (await res.json()) as { error?: string };
    if (data?.error) return data.error;
  } catch {
    /* keep the fallback */
  }
  return fallback;
}

function authError(status: number): Error | null {
  if (status === 401) return new Error("Your session expired — sign in again.");
  if (status === 403) return new Error("You do not have access to do that.");
  return null;
}

/** Create a folder (optionally inside `parentId`). */
export async function createFolder(
  name: string,
  parentId?: string | null
): Promise<{ folderId: string; name: string }> {
  const res = await fetch("/folder", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, parentId: parentId ?? null }),
    credentials: "same-origin",
  });
  if (!res.ok) {
    throw (
      authError(res.status) ??
      new Error(await errorMessage(res, `Could not create folder (${res.status}).`))
    );
  }
  return (await res.json()) as { folderId: string; name: string };
}

/** Apply a sharing change to a folder; resolves with the new viewers/editors. */
export async function setFolderAccess(
  folderId: string,
  change: AccessChange
): Promise<{ viewers: string[]; editors: string[] }> {
  const res = await fetch("/folder-access", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ folderId, ...change }),
    credentials: "same-origin",
  });
  if (!res.ok) {
    if (res.status === 403) {
      throw new Error("Only the folder owner can change sharing.");
    }
    throw (
      authError(res.status) ??
      new Error(await errorMessage(res, `Sharing update failed (${res.status}).`))
    );
  }
  return (await res.json()) as { viewers: string[]; editors: string[] };
}

export type OrganizeKind = "doc" | "folder";

async function organize(payload: {
  kind: OrganizeKind;
  id: string;
  op: "move" | "rename" | "delete";
  targetFolderId?: string | null;
  name?: string;
}): Promise<void> {
  const res = await fetch("/organize", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    credentials: "same-origin",
  });
  if (!res.ok) {
    throw (
      authError(res.status) ??
      new Error(await errorMessage(res, `Action failed (${res.status}).`))
    );
  }
}

/** Move a doc or folder into `targetFolderId` (null = root). */
export function moveItem(
  kind: OrganizeKind,
  id: string,
  targetFolderId: string | null
): Promise<void> {
  return organize({ kind, id, op: "move", targetFolderId });
}

/** Rename a doc (title) or folder (name). */
export function renameItem(
  kind: OrganizeKind,
  id: string,
  name: string
): Promise<void> {
  return organize({ kind, id, op: "rename", name });
}

/** Delete an (owned) folder; its children are reparented to its parent. */
export function deleteFolder(id: string): Promise<void> {
  return organize({ kind: "folder", id, op: "delete" });
}

/** Duplicate a doc you can view into a new owner-only copy (optionally in a folder). */
export async function duplicateDoc(
  docId: string,
  folderId?: string | null
): Promise<{ docId: string; versionId: string; title: string }> {
  const res = await fetch("/duplicate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ docId, folderId: folderId ?? null }),
    credentials: "same-origin",
  });
  if (!res.ok) {
    throw (
      authError(res.status) ??
      new Error(await errorMessage(res, `Could not duplicate (${res.status}).`))
    );
  }
  return (await res.json()) as { docId: string; versionId: string; title: string };
}
